/**
 * Encryption Service for securing cached data
 * Uses AES-256-GCM encryption with user-specific keys
 */

import CryptoJS from 'crypto-js'

interface EncryptedData {
  ciphertext: string
  iv: string
  salt: string
  tag?: string
}

class EncryptionService {
  private readonly PBKDF2_ITERATIONS = 100000
  private readonly KEY_SIZE = 256 / 32 // 256 bits
  private readonly SALT_SIZE = 128 / 8 // 128 bits
  private readonly SALT_VERSION = 'v1' // Version for salt algorithm

  // Cache derived keys to avoid expensive PBKDF2 on every operation
  // Key: `${email}:${saltBase64}`, Value: derived key
  private keyCache = new Map<string, CryptoJS.lib.WordArray>()
  private readonly MAX_CACHE_SIZE = 100 // Prevent memory leaks

  // Cache deterministic salts per user
  private saltCache = new Map<string, CryptoJS.lib.WordArray>()

  /**
   * Generate a deterministic salt from user email
   * This allows all data for a user to use the same salt, enabling key caching
   * across multiple encryption operations (critical for multi-sensor devices)
   */
  private getDeterministicSalt(userEmail: string): CryptoJS.lib.WordArray {
    // Check cache first
    const cached = this.saltCache.get(userEmail)
    if (cached) {
      return cached
    }

    // Generate consistent salt from user email using SHA-256
    // Include version string to allow salt algorithm changes in future
    const saltSource = `${userEmail}_salt_${this.SALT_VERSION}_${window.location.hostname}`
    const hash = CryptoJS.SHA256(saltSource)

    // Take first 128 bits (16 bytes) for the salt
    const salt = CryptoJS.lib.WordArray.create(
      hash.words.slice(0, this.SALT_SIZE / 4)
    )

    // Cache the salt
    this.saltCache.set(userEmail, salt)

    return salt
  }

  /**
   * Derive a consistent encryption key from user email and a secret
   * Uses caching to avoid expensive PBKDF2 computation on every operation
   * In production, the secret should come from a secure backend
   */
  private deriveKey(email: string, salt: CryptoJS.lib.WordArray): CryptoJS.lib.WordArray {
    // Generate cache key from email and salt
    const saltBase64 = salt.toString(CryptoJS.enc.Base64)
    const cacheKey = `${email}:${saltBase64}`

    // Check if we already have this key cached
    const cachedKey = this.keyCache.get(cacheKey)
    if (cachedKey) {
      return cachedKey
    }

    // Key not in cache - derive it (expensive operation)
    const passphrase = `${email}_${window.location.hostname}_ioag_encryption_v1`
    const derivedKey = CryptoJS.PBKDF2(passphrase, salt, {
      keySize: this.KEY_SIZE,
      iterations: this.PBKDF2_ITERATIONS,
      hasher: CryptoJS.algo.SHA256
    })

    // Cache the derived key
    this.keyCache.set(cacheKey, derivedKey)

    // Prevent memory leaks by limiting cache size
    if (this.keyCache.size > this.MAX_CACHE_SIZE) {
      // Remove oldest entry (first key in Map)
      const firstKey = this.keyCache.keys().next().value
      if (firstKey) {
        this.keyCache.delete(firstKey)
      }
    }

    return derivedKey
  }

  /**
   * Encrypt data using AES-256-CBC with a user-specific key
   * Uses deterministic salt per user to enable key caching across operations
   */
  encrypt(data: any, userEmail: string): EncryptedData {
    try {
      // Convert data to JSON string
      const plaintext = JSON.stringify(data)

      // Use deterministic salt for this user (enables key caching)
      const salt = this.getDeterministicSalt(userEmail)

      // Generate random IV (MUST be random for security)
      const iv = CryptoJS.lib.WordArray.random(128 / 8)

      // Derive key from email and salt (will be cached after first derivation)
      const key = this.deriveKey(userEmail, salt)

      // Encrypt the data
      const encrypted = CryptoJS.AES.encrypt(plaintext, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      })

      return {
        ciphertext: encrypted.ciphertext.toString(CryptoJS.enc.Base64),
        iv: iv.toString(CryptoJS.enc.Base64),
        salt: salt.toString(CryptoJS.enc.Base64)
      }
    } catch (error) {
      console.error('Encryption failed:', error)
      throw new Error('Failed to encrypt data')
    }
  }

  /**
   * Decrypt data using AES-256-CBC with a user-specific key
   */
  decrypt<T = any>(encryptedData: EncryptedData, userEmail: string): T {
    try {
      // Parse the stored values
      const salt = CryptoJS.enc.Base64.parse(encryptedData.salt)
      const iv = CryptoJS.enc.Base64.parse(encryptedData.iv)
      const ciphertext = CryptoJS.enc.Base64.parse(encryptedData.ciphertext)

      // Derive the same key
      const key = this.deriveKey(userEmail, salt)

      // Create cipher params
      const cipherParams = CryptoJS.lib.CipherParams.create({
        ciphertext: ciphertext
      })

      // Decrypt the data
      const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      })

      // Convert to string and parse JSON
      const plaintext = decrypted.toString(CryptoJS.enc.Utf8)

      if (!plaintext) {
        throw new Error('Decryption failed - invalid key or corrupted data')
      }

      return JSON.parse(plaintext)
    } catch (error) {
      console.error('Decryption failed:', error)
      throw new Error('Failed to decrypt data - invalid key or corrupted data')
    }
  }

  /**
   * Verify if data can be decrypted with the current user's key
   */
  canDecrypt(encryptedData: EncryptedData, userEmail: string): boolean {
    try {
      this.decrypt(encryptedData, userEmail)
      return true
    } catch {
      return false
    }
  }

  /**
   * Generate a hash of the user email for indexing purposes
   * This allows us to find user data without exposing the email
   */
  hashUserIdentifier(email: string): string {
    return CryptoJS.SHA256(email).toString(CryptoJS.enc.Hex)
  }

  /**
   * Check if encryption is available and working
   */
  isAvailable(): boolean {
    try {
      // Test encryption/decryption
      const testData = { test: 'data' }
      const testEmail = 'test@example.com'
      const encrypted = this.encrypt(testData, testEmail)
      const decrypted = this.decrypt(encrypted, testEmail)

      return JSON.stringify(testData) === JSON.stringify(decrypted)
    } catch {
      return false
    }
  }

  /**
   * Check if encryption should be used
   * Checks both encryption availability and user preference
   */
  shouldUseEncryption(): boolean {
    // Import at runtime to avoid circular dependency
    const { cacheSettingsService } = require('./cache/cacheSettingsService')
    return cacheSettingsService.isEncryptionEnabled() && this.isAvailable()
  }

  /**
   * Get encryption statistics for monitoring
   */
  getEncryptionStats(): {
    algorithm: string
    keySize: number
    mode: string
    pbkdf2Iterations: number
    isAvailable: boolean
    cachedKeys: number
  } {
    return {
      algorithm: 'AES-256-CBC',
      keySize: 256,
      mode: 'CBC',
      pbkdf2Iterations: this.PBKDF2_ITERATIONS,
      isAvailable: this.isAvailable(),
      cachedKeys: this.keyCache.size
    }
  }

  /**
   * Clear the key cache
   * Should be called on logout or when user changes
   */
  clearKeyCache(): void {
    this.keyCache.clear()
    this.saltCache.clear()
  }
}

// Export singleton instance
export const encryptionService = new EncryptionService()
export type { EncryptedData }